import { ChangeEvent, LabelHTMLAttributes, PropsWithChildren, useCallback, useState } from 'react';
import { cn } from '../../util';
import { Icon } from '../Icon';

type TextInputProps = {
  name?: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  initialValue?: string;
  label?: React.ReactNode;
  placeholder?: string;
  onChange: (e: string) => void;
  errorMessage?: string;
  disabled?: boolean;
  type?: 'text' | 'password';
  size?: 'm' | 'l';
  testId?: string;
  autoFocus?: boolean;
};

export const TextInput = (props: TextInputProps) => {
  const { label, placeholder, icon, errorMessage, onChange, initialValue, disabled, autoFocus, size = 'm' } = props;
  const [value, setValue] = useState(initialValue ?? '');

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange(e.target.value);
    },
    [setValue, onChange]
  );

  return (
    <div className="flex w-full flex-col">
      {label && <InputLabel htmlFor="input-field">{label}</InputLabel>}
      <div
        className={cn(
          'bg-primary border-input-border flex items-center rounded-md border',
          size === 'm' && 'max-h-[36px] px-3 py-2 text-sm',
          size === 'l' && 'text-md max-h-[52px] p-4'
        )}
      >
        {icon && (
          <div className="text-input-text flex h-6 items-center justify-center pr-2">
            <Icon type={icon} size={size} />
          </div>
        )}
        <input
          name={props.name}
          type={props.type || 'text'}
          className={cn(
            `text-input-text placeholder:text-muted-foreground/80 w-full flex-1 bg-transparent leading-5 focus:outline-none`,
            disabled && `text-input-disabled-text cursor-not-allowed`
          )}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          autoFocus={autoFocus}
          disabled={disabled}
          data-testid={props.testId}
        />
      </div>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

export const PasswordInput = (props: TextInputProps) => {
  return <TextInput {...props} type="password" />;
};

export const InputLabel = ({
  children,
  required,
  ...props
}: PropsWithChildren & LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }) => {
  return (
    <label
      className={cn('mb-1 block text-sm text-white', required && "after:text-error after:ml-0.5 after:content-['*']")}
      {...props}
    >
      {children}
    </label>
  );
};

export const ErrorMessage = ({ children }: PropsWithChildren) => {
  return <div className="text-error mt-2 text-sm">{children}</div>;
};
